import React, { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DashboardWrapper from "../../components/DashboardWrapper";
import CrudTable from "../../components/dashboard/table/CrudTable";
import CrudForm from "../../components/dashboard/form/CrudForm";
import { useCrud } from "../../hooks/useCrud";
import Swal from "sweetalert2";
import { validateCost, validateName } from "../../utils/Validations";

export default function ShippingRule() {
  const navigate = useNavigate();
const [searchText, setSearchText] = useState("");
  const {
    data: rules,
    loading,
    createItem,
    updateItem,
    deleteItem,
  } = useCrud(
    `${process.env.REACT_APP_API_URL}/backend/order-shipping-rules`,
    []
  );

  const [editingItem, setEditingItem] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // ✅ Fields allowed in form
  const allowedFields = useMemo(
    () => ["name", "type", "min_cost", "cost", "status"],
    []
  );

  // ✅ Initial form data
  const getFormData = useCallback((item) => {
    return {
      name: item?.name || "",
      type: item?.type || "flat_cost",
      min_cost: item?.min_cost ?? "",
      cost: item?.cost ?? "",
      status: Number(item?.status ?? 1),
    };
  }, []);

  // ✅ SweetAlert helper
 const showSwal = (title, text, icon) => {
  Swal.fire({
    title,
    text,
    icon,
    confirmButtonText: "OK",
    customClass: {
      confirmButton: "btn btn-primary",
    },
    buttonsStyling: false,

    didOpen: () => {
      const container = document.querySelector(".swal2-container");

      if (container) {
        container.style.zIndex = "20000";
      }
    },
  });
};

const handleSubmit = async (formData) => {
  // ==========================================
  // NAME VALIDATION
  // ==========================================

  const nameError = validateName(formData.name);

  if (nameError !== true) {
    showSwal(
      "Validation Error!",
      nameError,
      "warning"
    );
    return;
  }

  // ==========================================
  // MIN COST VALIDATION
  // Only required for min_cost shipping type
  // ==========================================

  const isMinCost = formData.type === "min_cost";

  if (isMinCost) {
    const minCostError = validateCost(
      formData.min_cost,
      "Minimum cost"
    );

    if (minCostError !== true) {
      showSwal(
        "Validation Error!",
        minCostError,
        "warning"
      );
      return;
    }
  }

  // ==========================================
  // SHIPPING COST VALIDATION
  // Required for both Create and Edit
  // ==========================================

  const costError = validateCost(
    formData.cost,
    "Shipping cost"
  );

  if (costError !== true) {
    showSwal(
      "Validation Error!",
      costError,
      "warning"
    );
    return;
  }

  // ==========================================
  // TRIM NAME
  // ==========================================

  const dataToSend = {
    name: formData.name.trim(),
    type: formData.type,

    min_cost: isMinCost
      ? Number(formData.min_cost)
      : null,

    cost: Number(formData.cost),

    status: Number(formData.status),
  };

  try {
    // ==========================================
    // EDIT
    // ==========================================

    if (editingItem) {
      await updateItem(editingItem.id, dataToSend);

      showSwal(
        "Updated!",
        "Shipping rule updated successfully.",
        "success"
      );
    }

    // ==========================================
    // CREATE
    // ==========================================

    else {
      await createItem(dataToSend);

      showSwal(
        "Created!",
        "Shipping rule created successfully.",
        "success"
      );
    }

    setModalOpen(false);
    setEditingItem(null);

  } catch (error) {
    showSwal(
      "Error!",
      error.message || "Something went wrong.",
      "error"
    );
  }
};

  // ✅ Modal controls
  const openModal = (item = null) => {
    setEditingItem(item);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingItem(null);
  };

  // ✅ Delete handler
  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      customClass: {
        confirmButton: "btn btn-primary m-1",
        cancelButton: "btn btn-danger m-1",
      },
      buttonsStyling: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteItem(id);
          showSwal("Deleted!", "Shipping rule deleted.", "success");
        } catch (error) {
          showSwal("Error!", error.message || "Delete failed.", "error");
        }
      }
    });
  };

  // ✅ Status toggle
  const handleToggleStatus = async (item) => {
    try {
      await updateItem(item.id, {
        ...item,
        status: item.status ? 0 : 1,
      });
      showSwal("Success!", "Status updated.", "success");
    } catch {
      showSwal("Error!", "Failed to update status.", "error");
    }
  };

  // ✅ Table data
  const tableData = useMemo(
    () => rules.map((item, index) => ({ ...item, sl: index + 1 })),
    [rules]
  );

  return (
    <DashboardWrapper>
      <div className="container-fluid mt-4">
        <div className="d-flex justify-content-between mb-4">
          <button
            className="btn btn-secondary"
            onClick={() => navigate(-1)}
          >
            Back
          </button>

          <button
            className="btn btn-primary"
            onClick={() => openModal()}
          >
            Create Shipping Rule
          </button>
        </div>

        <div className="card shadow">
                             <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-3">
          <h4 className="mb-0">Shipping Rules</h4>

  <input
    type="text"
    className="form-control"
    placeholder="Search ..."
    value={searchText}
    onChange={(e) => setSearchText(e.target.value)}
    style={{
      width: "260px",
      maxWidth: "100%",
      borderRadius: "6px",
    }}
  />
</div>

          {loading ? (
            <div className="text-center p-5">
              <div className="spinner-border text-primary" />
            </div>
          ) : (
            <div className="card-body">
              <CrudTable
                data={tableData}
                columns={["sl", "name", "type", "min_cost", "cost", "status"]}
                onEdit={openModal}
                onDelete={handleDelete}
                onToggleStatus={handleToggleStatus}
                  searchText={searchText}
              />
            </div>
          )}
        </div>

        {modalOpen && (
          <div
            className="modal fade show"
            style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {editingItem
                      ? "Edit Shipping Rule"
                      : "Create Shipping Rule"}
                  </h5>
                  <button className="btn-close" onClick={closeModal} />
                </div>

                <div className="modal-body">
                  <CrudForm
                    initialData={getFormData(editingItem)}
                    onSubmit={handleSubmit}
                    allowedFields={allowedFields}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardWrapper>
  );
}
